import * as React from 'react'
import { useFormContext } from 'react-hook-form'

import {
  Badge,
  Box,
  CollapsableCard,
  Divider,
  Text,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { F0Button, F0Icon, F0Select } from '@factorialco/f0-react'
import { ChevronDown, ChevronRight, Delete, Plus, TextSize } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import { getFieldErrors } from 'modules/workflows/components/Node/utils'
import { VariablesConfig } from 'modules/workflows/components/Sidepanel/VariableSelectorTypes'
import { ConditionalFormSchema } from 'modules/workflows/components/Sidepanel/schemas/conditionalFormSchema'
import { useWorkflowContext } from 'modules/workflows/contexts/WorkflowContext'
import { WorkflowsComparison, WorkflowsExpression } from 'modules/workflows/lib/types'

import { RightHandSelector, VariableData } from './RightHandSelector'
import { DEFAULT_COMPARISON, EMPTY_ARRAY_SELECTOR_VALUE, EMPTY_SELECTOR_VALUE } from './constants'
import { getComparisonErrorMessages } from './errors'
import { getLiteralType } from './literalTypes'
import { getOperatorValuesForVariable } from './operators'
import { LiteralTypeEnum, OperatorEnum, VariableTypeEnum } from './types'

type WorkflowDataInput = {
  name: string
  simpleType?: { kind: VariableTypeEnum } | null
  enumType?: { options: Array<string> } | null
}

const extractVariableType = (input?: WorkflowDataInput): VariableTypeEnum | undefined => {
  return input?.simpleType?.kind ?? (input?.enumType ? 'string' : undefined)
}

const ConditionalPathCardContent = ({
  comparisonPos,
  expression,
  setExpression,
  variablesConfig,
  conditionsHeaderLabel = i18n.t('workflows.sidepanel.condition.paths.conditions_header'),
  dynamicInputsHeaderLabel = i18n.t('workflows.sidepanel.condition.paths.dynamic_inputs_header'),
}: {
  comparisonPos: number
  expression: WorkflowsExpression
  setExpression: (newExpression: WorkflowsExpression) => void
  variablesConfig: VariablesConfig
  conditionsHeaderLabel?: string
  dynamicInputsHeaderLabel?: string
}) => {
  const { workflowManifest } = useWorkflowContext()
  const { formState, getFieldState, clearErrors } = useFormContext<ConditionalFormSchema>()

  const operatorLabel = React.useCallback((value: OperatorEnum): string => {
    switch (value) {
      case 'eq':
        return i18n.t('workflows.node_type.condition.operations.eq')
      case 'neq':
        return i18n.t('workflows.node_type.condition.operations.neq')
      case 'one_of':
        return i18n.t('workflows.node_type.condition.operations.one_of')
      case 'not_one_of':
        return i18n.t('workflows.node_type.condition.operations.not_one_of')
      case 'gt':
        return i18n.t('workflows.node_type.condition.operations.gt')
      case 'gte':
        return i18n.t('workflows.node_type.condition.operations.gte')
      case 'lt':
        return i18n.t('workflows.node_type.condition.operations.lt')
      case 'lte':
        return i18n.t('workflows.node_type.condition.operations.lte')
      default:
        return String(value)
    }
  }, [])

  const operatorOptionsFor = React.useCallback(
    (variableName?: string) =>
      getOperatorValuesForVariable(variablesConfig, variableName).map((value) => ({
        value,
        label: operatorLabel(value),
      })),
    [operatorLabel, variablesConfig]
  )

  const getVariableData = React.useCallback(
    (name?: string, source?: 'input' | 'dynamic_input'): VariableData | undefined => {
      if (!name || !source) return undefined

      const input =
        source === 'input'
          ? workflowManifest.workflowDataInputs.find((i) => i.name === name)
          : workflowManifest.dynamicWorkflowDataInputs.find((i) => i.name === name)

      if (!input) return undefined

      return {
        name: input.name,
        type: extractVariableType(input) ?? 'string',
        options: input.enumType?.options ?? null,
        unit: null,
      }
    },
    [workflowManifest.workflowDataInputs, workflowManifest.dynamicWorkflowDataInputs]
  )

  const staticInputVariables = workflowManifest.workflowDataInputs.map((input) => {
    const config = variablesConfig && variablesConfig[input.name]
    return {
      label: config?.label ?? input.name,
      value: input.name,
      source: 'input' as const,
      icon: config?.icon,
    }
  })

  const dynamicInputVariables = workflowManifest.dynamicWorkflowDataInputs.map((input) => {
    const configLabel = variablesConfig && variablesConfig[input.name]?.label
    return {
      label: configLabel ?? input.name,
      value: input.name,
      source: 'dynamic_input' as const,
    }
  })

  // Grouped options for the select dropdown (matching the design)
  const selectOptions = React.useMemo(() => {
    const options: Array<
      | { label: string; value: string; disabled?: boolean; icon?: typeof TextSize }
      | { type: 'separator' }
    > = []

    // Add "Conditions" group (static workflow inputs)
    if (staticInputVariables.length > 0) {
      options.push({
        value: 'header_conditions',
        label: conditionsHeaderLabel,
        disabled: true,
      })
      staticInputVariables
        .sort((a, b) => a.label.localeCompare(b.label))
        .forEach((variable) => {
          options.push({
            label: variable.label,
            value: variable.value,
            icon: variable.icon,
          })
        })
    }

    // Add "Form questions" group (dynamic inputs)
    if (dynamicInputVariables.length > 0) {
      if (staticInputVariables.length > 0) {
        options.push({ type: 'separator' })
      }
      options.push({
        value: 'header_dynamic_inputs',
        label: dynamicInputsHeaderLabel,
        disabled: true,
      })
      dynamicInputVariables
        .sort((a, b) => a.label.localeCompare(b.label))
        .forEach((variable) => {
          options.push({ label: variable.label, value: variable.value, icon: TextSize })
        })
    }

    return options
  }, [staticInputVariables, dynamicInputVariables, conditionsHeaderLabel, dynamicInputsHeaderLabel])

  const comparisons = expression.root.expressions
  const firstComparison = comparisons[0]

  const handleDeleteComparison = (comparisonIndex: number) => {
    const newExpressions = comparisons.filter((_, index) => index !== comparisonIndex)

    setExpression({
      ...expression,
      root: {
        ...expression.root,
        expressions: newExpressions,
      },
    })
  }

  const handleLiteralSelection = (
    comparisonIndex: number,
    newValue: string,
    type: LiteralTypeEnum
  ) => {
    // Validate the value based on type
    // For number types, allow empty string (for in-progress editing) or valid numbers
    if (type === 'integer' || type === 'float') {
      // Allow empty string or valid number format (including negative and decimal)
      if (newValue !== '' && !/^-?\d*\.?\d*$/.test(newValue)) {
        // If invalid format, don't update
        return
      }
    }

    const newExpressions = comparisons.map((comparison, index) => {
      if (index === comparisonIndex) {
        return {
          ...comparison,
          right: { value: newValue, type },
        }
      }
      return comparison
    })

    setExpression({
      ...expression,
      root: {
        ...expression.root,
        expressions: newExpressions,
      },
    })
  }

  const handleVariableSelection = (comparisonIndex: number, variableName: string) => {
    // Check if it's a static input first, then dynamic input
    const staticInput = workflowManifest.workflowDataInputs.find(
      (data) => data.name === variableName
    )
    const dynamicInput = workflowManifest.dynamicWorkflowDataInputs.find(
      (data) => data.name === variableName
    )

    const variableData = staticInput ?? dynamicInput

    if (!variableData) {
      throw new Error(`Variable data not found for: ${variableName}`)
    }

    const source: 'input' | 'dynamic_input' = staticInput ? 'input' : 'dynamic_input'

    // Whenever variable changes, reset operator to "is equal to" to avoid stale/invalid operator state.
    const nextOperator: OperatorEnum = 'eq'
    const type = getLiteralType(extractVariableType(variableData), nextOperator)
    const nextValue = EMPTY_SELECTOR_VALUE

    const newExpressions: WorkflowsComparison[] = comparisons.map((comparison, index) => {
      if (index === comparisonIndex) {
        return {
          ...comparison,
          left: { name: variableName, source: source },
          operator: nextOperator,
          right: { value: nextValue, type: type },
        }
      }
      return comparison
    })

    // Clear validation errors for this expression when variable changes
    // This prevents stale errors from a previous variable selection from persisting
    clearErrors(`comparisons.${comparisonPos}.expression.root.expressions.${comparisonIndex}`)

    setExpression({
      ...expression,
      root: {
        ...expression.root,
        expressions: newExpressions,
      },
    })
  }

  const handleOperatorSelection = (comparisonIndex: number, operator: OperatorEnum) => {
    const newExpressions = comparisons.map((comparison, index) => {
      if (index === comparisonIndex) {
        const previousOperator = comparison.operator
        const isArrayOperator = (op: string) => op === 'one_of' || op === 'not_one_of'
        const switchingBetweenSingleAndMultiSelect =
          isArrayOperator(previousOperator) !== isArrayOperator(operator)

        // Reset value when switching between single and multi-select modes
        if (switchingBetweenSingleAndMultiSelect) {
          const variableData =
            workflowManifest.workflowDataInputs.find(
              (data) => data.name === comparison.left.name
            ) ??
            workflowManifest.dynamicWorkflowDataInputs.find(
              (data) => data.name === comparison.left.name
            )

          const newType = getLiteralType(extractVariableType(variableData), operator)
          const newValue = isArrayOperator(operator)
            ? EMPTY_ARRAY_SELECTOR_VALUE
            : EMPTY_SELECTOR_VALUE

          return {
            ...comparison,
            operator: operator,
            right: { value: newValue, type: newType },
          }
        }

        return {
          ...comparison,
          operator: operator,
        }
      }
      return comparison
    })

    setExpression({
      ...expression,
      root: {
        ...expression.root,
        expressions: newExpressions,
      },
    })
  }

  const handleAddRequirement = () => {
    const newExpressions = [...comparisons, DEFAULT_COMPARISON]

    setExpression({
      ...expression,
      root: {
        ...expression.root,
        expressions: newExpressions,
      },
    })
  }

  const errors = getFieldState(
    `comparisons.${comparisonPos}.expression.root.expressions.0`,
    formState
  ).error
  const { leftMessage, operatorMessage, rightMessage } = getComparisonErrorMessages(errors)
  const { variable, operator, value } = getFieldErrors(expression, 0)

  return (
    <Box
      border={{ top: { width: 's1', color: 'grey300', style: 'solid' } }}
      padding="s16"
      gap="s16"
    >
      <Box flexDirection="row" gap="s8" alignItems="flexStart" width="full">
        <Box style={{ flex: '1', minWidth: '0' }}>
          <F0Select
            size="md"
            hideLabel
            clearable={false}
            label={i18n.t('workflows.sidepanel.condition.paths.type')}
            placeholder={i18n.t('workflows.sidepanel.condition.paths.select_placeholder')}
            options={selectOptions}
            value={firstComparison?.left.name}
            onChange={(variableName: string) => handleVariableSelection(0, variableName)}
            error={variable ? leftMessage : undefined}
          />
        </Box>

        <Box style={{ flex: '1', minWidth: '0' }}>
          <F0Select
            size="md"
            hideLabel
            clearable={false}
            disabled={!firstComparison?.left.name}
            label="Operator"
            placeholder={i18n.t('workflows.sidepanel.condition.paths.operator_placeholder')}
            options={operatorOptionsFor(firstComparison?.left.name)}
            value={firstComparison?.operator}
            onChange={(op: OperatorEnum) => handleOperatorSelection(0, op)}
            error={operator ? operatorMessage : undefined}
          />
        </Box>

        <Box style={{ flex: '1', minWidth: '0' }}>
          <RightHandSelector
            variableName={firstComparison?.left.name}
            value={firstComparison?.right.value}
            operator={firstComparison?.operator}
            disabled={!firstComparison?.left.name || !firstComparison?.operator}
            onChange={(newValue) => {
              const firstComparisonLeftName = firstComparison?.left.name
              if (!firstComparisonLeftName) return

              const variableData =
                workflowManifest.workflowDataInputs.find(
                  (data) => data.name === firstComparisonLeftName
                ) ??
                workflowManifest.dynamicWorkflowDataInputs.find(
                  (data) => data.name === firstComparisonLeftName
                )
              const type = getLiteralType(
                extractVariableType(variableData),
                firstComparison?.operator
              )

              handleLiteralSelection(0, newValue, type)
            }}
            variablesConfig={variablesConfig}
            error={value ? rightMessage : undefined}
            variableData={getVariableData(firstComparison?.left.name, firstComparison?.left.source)}
          />
        </Box>
      </Box>

      {/* Requirements list */}
      <Box
        width="fit-content"
        border={{ all: { width: 's1', color: 'grey300', style: 'solid' } }}
        borderRadius={{ all: 'abs100' }}
        paddingX="s8"
        paddingY="s2"
        inline
      >
        <Text size="100" color="textSecondary" weight="medium">
          {i18n.t('workflows.sidepanel.condition.paths.and')}
        </Text>
      </Box>

      <Box flexDirection="row" gap="s12" alignItems="stretch" paddingLeft={'s20'} width="full">
        <Divider vertical />

        <Box flexGrow gap="s4" style={{ flex: '1', minWidth: '0' }}>
          {comparisons.map((comparison, index) => {
            if (index === 0) return null

            const indexedErrors = getFieldState(
              `comparisons.${comparisonPos}.expression.root.expressions.${index}`,
              formState
            ).error

            const {
              leftMessage: indexedLeftMessage,
              operatorMessage: indexedOperatorMessage,
              rightMessage: indexedRightMessage,
            } = getComparisonErrorMessages(indexedErrors)

            const {
              variable: indexedVariable,
              operator: indexedOperator,
              value: indexedValue,
            } = getFieldErrors(expression, index)

            return (
              <Box
                key={`req-${index}`}
                flexDirection="row"
                gap="s8"
                alignItems="flexStart"
                paddingBottom="s8"
                width="full"
                justifyContent="flexStart"
              >
                <Box style={{ flex: '1', minWidth: '0' }}>
                  <F0Select
                    size="md"
                    hideLabel
                    clearable={false}
                    label="field"
                    placeholder={i18n.t('workflows.sidepanel.condition.paths.select_placeholder')}
                    options={selectOptions}
                    value={comparison.left.name}
                    onChange={(variableName: string) =>
                      handleVariableSelection(index, variableName)
                    }
                    error={indexedVariable ? indexedLeftMessage : undefined}
                  />
                </Box>

                <Box style={{ flex: '1', minWidth: '0' }}>
                  <F0Select
                    size="md"
                    hideLabel
                    clearable={false}
                    disabled={!comparison.left.name}
                    label="equals"
                    placeholder={i18n.t('workflows.sidepanel.condition.paths.operator_placeholder')}
                    options={operatorOptionsFor(comparison.left.name)}
                    value={comparison.operator}
                    onChange={(op: OperatorEnum) => handleOperatorSelection(index, op)}
                    error={indexedOperator ? indexedOperatorMessage : undefined}
                  />
                </Box>

                <Box style={{ flex: '1', minWidth: '0' }}>
                  <RightHandSelector
                    variableName={comparison.left.name}
                    value={comparison.right.value}
                    operator={comparison.operator}
                    disabled={!comparison.left.name || !comparison.operator}
                    onChange={(newValue) => {
                      const comparisonLeftName = comparison.left.name
                      if (!comparisonLeftName) return

                      const variableData =
                        workflowManifest.workflowDataInputs.find(
                          (data) => data.name === comparisonLeftName
                        ) ??
                        workflowManifest.dynamicWorkflowDataInputs.find(
                          (data) => data.name === comparisonLeftName
                        )
                      const type = getLiteralType(
                        extractVariableType(variableData),
                        comparison.operator
                      )

                      handleLiteralSelection(index, newValue, type)
                    }}
                    variablesConfig={variablesConfig}
                    error={indexedValue ? indexedRightMessage : undefined}
                    variableData={getVariableData(comparison.left.name, comparison.left.source)}
                  />
                </Box>

                <Box style={{ flexShrink: '0' }} paddingTop="s4">
                  <F0Button
                    icon={Delete}
                    label={i18n.t('common.delete')}
                    hideLabel
                    onClick={() => handleDeleteComparison(index)}
                    size="md"
                    variant="critical"
                  />
                </Box>
              </Box>
            )
          })}

          <Box flexDirection="row" gap="s8" alignItems="flexStart" paddingBottom="s8">
            <F0Button
              label={i18n.t('workflows.sidepanel.condition.paths.add_requirement')}
              icon={Plus}
              variant="ghost"
              size="sm"
              disabled={Object.values(getFieldErrors(expression, 0)).some((error) => error)}
              onClick={handleAddRequirement}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export const ConditionPathCard = ({
  comparisonPos,
  expression,
  setExpression,
  variablesConfig,
  priority,
  onRemove,
  canRemove,
  conditionsHeaderLabel,
  dynamicInputsHeaderLabel,
}: {
  comparisonPos: number
  expression: WorkflowsExpression
  setExpression: (newExpression: WorkflowsExpression) => void
  variablesConfig: VariablesConfig
  priority: number
  onRemove: () => void
  canRemove: boolean
  conditionsHeaderLabel?: string
  dynamicInputsHeaderLabel?: string
}) => {
  const [open, toggle] = useToggle(true)

  return (
    <CollapsableCard
      open={open}
      Card={
        <Box
          flexDirection="row"
          alignItems="center"
          justifyContent="spaceBetween"
          width="full"
          cursor="pointer"
          onClick={toggle(!open)}
        >
          <Box flexDirection="row" gap="s8" alignItems="center">
            <Badge>{priority}</Badge>
            <Text size="200" weight="medium">
              {i18n.t('workflows.sidepanel.condition.paths.path')}
            </Text>
            <F0Icon icon={open ? ChevronDown : ChevronRight} size="md" />
          </Box>
          {canRemove && (
            <F0Button
              icon={Delete}
              label={i18n.t('common.delete')}
              hideLabel
              onClick={onRemove}
              size="md"
              variant="critical"
            />
          )}
        </Box>
      }
      Content={
        open && (
          <ConditionalPathCardContent
            comparisonPos={comparisonPos}
            expression={expression}
            setExpression={setExpression}
            variablesConfig={variablesConfig}
            conditionsHeaderLabel={conditionsHeaderLabel}
            dynamicInputsHeaderLabel={dynamicInputsHeaderLabel}
          />
        )
      }
      contentPadding="s0"
      contentBackground="grey000White"
    />
  )
}

export const ConditionPathHeader = ({ onAddCondition }: { onAddCondition: () => void }) => {
  return (
    <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
      <Text size="300" weight="medium" color="textPrimary">
        {i18n.t('workflows.sidepanel.condition.paths.title')}
      </Text>
      <F0Button
        label={i18n.t('workflows.sidepanel.condition.paths.new')}
        icon={Plus}
        variant="outline"
        size="md"
        onClick={onAddCondition}
      />
    </Box>
  )
}
